<template>
  <v-container class="submission-table" fluid>
    <div class="submission-table__header">
      <h1 class="submission-table__title">{{ title }}</h1>
      <div class="submission-table__actions">
        <v-btn variant="text" class="text-none" @click="goToList">Back to forms</v-btn>
        <v-btn
          color="primary"
          variant="flat"
          class="text-none"
          prepend-icon="mdi-download"
          :loading="downloading"
          :disabled="downloading || total === 0"
          @click="downloadCsv"
        >
          Download CSV
        </v-btn>
      </div>
    </div>

    <v-alert
      v-if="loadError"
      type="error"
      variant="tonal"
      class="mb-4"
    >
      {{ loadError }}
      <div class="mt-3">
        <v-btn variant="text" color="primary" class="text-none" @click="reload">Retry</v-btn>
      </div>
    </v-alert>

    <v-alert
      v-if="exportError"
      type="error"
      variant="tonal"
      class="mb-4"
    >
      {{ exportError }}
      <div class="mt-3">
        <v-btn variant="text" color="primary" class="text-none" @click="downloadCsv">Retry download</v-btn>
      </div>
    </v-alert>

    <div class="submission-table__scroll">
      <v-data-table-server
        :headers="headers"
        :items="rows"
        :items-length="total"
        :loading="loading"
        :page="page"
        :items-per-page="pageSize"
        :items-per-page-options="[10, 25, 50]"
        item-value="_id"
        :row-props="rowProps"
        @update:options="onOptions"
      >
        <template #[`item._needsAttention`]="{ item }">
          <v-chip
            v-if="item._needsAttention"
            color="warning"
            size="small"
            prepend-icon="mdi-alert"
            variant="flat"
          >
            Needs attention
          </v-chip>
          <span v-else class="submission-table__ok">—</span>
        </template>

        <template #no-data>
          <div class="submission-table__empty">No submissions yet.</div>
        </template>
      </v-data-table-server>
    </div>
  </v-container>
</template>

<script>
// SubmissionTable — admin view of a form's submissions. Server-paginated (the
// history is unbounded), defect rows highlighted, CSV export via the server.
import AdminFormApi from '@/services/AdminFormApi'

function formatAnswer (value) {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return value.join('; ')
  return String(value)
}

export default {
  name: 'SubmissionTable',
  props: {
    slug: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      formMeta: null, // { slug, title, status, questions }
      submissions: [],
      total: 0,
      page: 1,
      pageSize: 25,
      loading: false,
      loadError: '',
      exportError: '',
      downloading: false,
      // Monotonic token so only the newest in-flight load may mutate state —
      // rapid pagination can otherwise resolve out of order (stale overwrite).
      requestSeq: 0,
    }
  },
  watch: {
    // The route reuses this component across :slug changes (same route record),
    // so created() does not re-run. Reset and reload when the slug changes.
    slug () {
      this.formMeta = null
      this.submissions = []
      this.total = 0
      this.load(1, this.pageSize)
    },
  },
  computed: {
    title () {
      return this.formMeta ? `${this.formMeta.title} — submissions` : 'Submissions'
    },
    questions () {
      return (this.formMeta && this.formMeta.questions) || []
    },
    // Meta columns use "_"-prefixed keys so they never collide with a question
    // key (e.g. the prestart has a question keyed "vehicle").
    headers () {
      const meta = [
        { title: 'Submitted', key: '_submittedAt', sortable: false },
        { title: 'Submitter', key: '_submitter', sortable: false },
        { title: 'Needs attention', key: '_needsAttention', sortable: false },
      ]
      const questionCols = this.questions.map((q) => ({
        title: q.label, key: q.key, sortable: false,
      }))
      return meta.concat(questionCols)
    },
    // Flatten each submission for the table: meta under "_" keys, answers at the
    // top level keyed by question key.
    rows () {
      return this.submissions.map((s) => {
        const row = {
          _id: s.id,
          _submittedAt: s.submittedAt ? new Date(s.submittedAt).toLocaleString() : '',
          _submitter: s.submitter || '',
          _needsAttention: !!s.needsAttention,
        }
        for (const q of this.questions) {
          row[q.key] = formatAnswer(s.answers ? s.answers[q.key] : undefined)
        }
        return row
      })
    },
  },
  methods: {
    // Fired by v-data-table-server on mount and whenever page / size changes.
    onOptions (options) {
      const page = options.page || 1
      const pageSize = options.itemsPerPage || this.pageSize
      this.load(page, pageSize)
    },
    async load (page, pageSize) {
      // Reflect the requested view immediately so a failed Retry re-requests THIS
      // page/size (not the last successful one) and the chrome matches the ask.
      this.page = page
      this.pageSize = pageSize
      const seq = ++this.requestSeq
      this.loading = true
      this.loadError = ''
      try {
        const res = await AdminFormApi.listSubmissions(this.slug, { page, pageSize })
        if (seq !== this.requestSeq) return // superseded by a newer request
        const data = res.data || {}
        this.formMeta = data.form || this.formMeta
        this.submissions = data.submissions || []
        this.total = data.total || 0
        // Honour the server's normalised page/size (e.g. clamped past-end page).
        if (data.page) this.page = data.page
        if (data.pageSize) this.pageSize = data.pageSize
      } catch (err) {
        if (seq !== this.requestSeq) return
        this.loadError = (err && err.response && err.response.status === 404)
          ? 'This form was not found.'
          : 'Submissions could not be loaded.'
        // Keep the last known rows/total rather than blanking the table on a
        // transient failure — the user can Retry the same page.
      } finally {
        if (seq === this.requestSeq) this.loading = false
      }
    },
    reload () {
      this.load(this.page, this.pageSize)
    },
    rowProps ({ item }) {
      return item._needsAttention ? { class: 'submission-table__row--flagged' } : {}
    },
    async downloadCsv () {
      this.downloading = true
      this.exportError = ''
      let url = null
      try {
        const res = await AdminFormApi.exportSubmissions(this.slug)
        const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' })
        url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${this.slug}-submissions.csv`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      } catch (err) {
        this.exportError = 'The CSV export failed. Please try again.'
      } finally {
        // Revoke even if the download threw, so the object URL never leaks.
        if (url) URL.revokeObjectURL(url)
        this.downloading = false
      }
    },
    goToList () {
      this.$router.push({ name: 'forms' })
    },
  },
}
</script>

<style scoped>
.submission-table {
  padding-top: 16px;
  padding-bottom: 48px;
}

.submission-table__header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.submission-table__title {
  font-size: 1.5rem;
  font-weight: 800;
  color: #41281b;
}

.submission-table__actions {
  display: flex;
  gap: 8px;
}

/* Wide tables scroll horizontally rather than forcing the page to. */
.submission-table__scroll {
  overflow-x: auto;
}

.submission-table__ok {
  color: #929292;
}

.submission-table__empty {
  padding: 24px;
  color: #929292;
  text-align: center;
}

:deep(.submission-table__row--flagged) {
  background-color: rgba(var(--v-theme-warning), 0.12);
}
</style>
